import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, MenuItem, Typography, Alert } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { referenceService } from "src/api/references/referenceService";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { useTranslate } from "src/locales";

interface LabRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (value: string) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const LabRequestModal: React.FC<LabRequestModalProps> = ({
  open,
  onClose,
  onSave,
  // mcpId artık kullanılmıyor; numune yeri seçenekleri AdditionalInfo/fields üzerinden geliyor
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));
  const [sampleRegion, setSampleRegion] = useState<string>("");
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);

  // Modal açıldığında state temizle
  useEffect(() => {
    if (!open) return;
    setSampleRegion("");
    setError("");
  }, [open]);

  // AdditionalInfo/fields üzerinden LAB_REQUEST form tanımını yükle
  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        setLoading(true);
        const res: any = await referenceService.additionalInfo.getFields({
          formtype: "LAB_REQUEST",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        setAdditionalInfo(res);

        const fields = res.fields || [];
        const field =
          fields.find((f: any) => f.fieldname === "LabSampleTakenSiteId") ||
          (fields.length > 0 ? fields[0] : null);

        if (field && Array.isArray(field.options)) {
          setOptions(
            field.options.map((opt: any) => ({
              label: opt.label ?? "",
              value: String(opt.value),
            }))
          );
        } else {
          setOptions([]);
          setError("Numune alınan bölge listesi bulunamadı.");
        }
      } catch {
        setOptions([]);
        setError("Numune alınan bölge listesi yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    if (!sampleRegion) return;
    onSave?.(sampleRegion);
    if (!onSave) {
    onClose();
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.lab.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={!sampleRegion} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} width={320}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.lab.sample-region")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.lab.sample-region")}
              value={
                options.find((opt) => opt.value === sampleRegion)?.label || ""
              }
              selectedId={sampleRegion || undefined}
              dialogMaxHeight="60vh"
              staticData={options.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  setSampleRegion(String(first.id));
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default LabRequestModal;
